<template>
  <q-card
    class="column cursor-pointer overflow-hidden"
    style="min-height: 160px;"
    :style="{
      width: width,
      'max-width': dialog || $q.screen.lt.sm ? undefined : '300px',
      border: itemCount(item) ? '3px solid var(--q-primary)' : undefined
    }"
    flat
    bordered
    data-test-id="featured-item-card"
    @click="addItem(item)"
  >
    <div>
      <transition
        leave-class="hidden"
        enter-active-class="animated bounceIn"
      >
        <q-badge
          v-if="itemCount(item)"
          :key="itemCount(item)"
          style="font-size: 18px; z-index: 1"
          class="q-py-sm text-bold"
          color="primary"
          floating
        >
          x {{ itemCount(item) }}
        </q-badge>
      </transition>
    </div>

    <q-card-section
      horizontal
      style="height: 100%;"
      class="overflow-hidden"
    >
      <age-restricted-banner
        v-if="item.showAlcoholAcknowledgement"
      />
      <q-img
        v-if="!!item.imageUrl"
        class="col-7"
        :src="item.imageUrl"
      />
      <q-card-section
        class=" column justify-between q-pa-md"
        :class="!!item.imageUrl ? 'col-5' : 'col-12'"
        style="height: 100%;"
      >
        <div class="text-body1 text-left text-weight-bolder">
          {{ descriptionForItem(item) }}
        </div>
        <div
          v-if="!!priceForItem(item)"
          class="text-body1 text-right"
        >
          <i18n-n
            :value="priceForItem(item)"
            format="currency"
            scope="global"
          />
        </div>
      </q-card-section>
    </q-card-section>
    <order-item-form
      ref="orderItemForm"
      :available-online="availableOnline"
      @add-model="orderItem => notifyAdded(orderItem.itemOrderItem.item)"
    />
    <alcohol-acknowledgement-dialog
      ref="alcoholAcknowledgementDialog"
      @alcohol-acknowledgement-concerned="checkItemSalesRestrictions(item)"
    />
  </q-card>
</template>

<script>
import { Cookies, Notify } from 'quasar'
import AgeRestrictedBanner from 'src/modules/checkout/components/AgeRestrictedBanner'
import AlcoholAcknowledgementDialog from 'src/modules/checkout/components/AlcoholAcknowledgementDialog'
import SalesRestrictionsMixin from 'src/modules/mixins/SalesRestrictionsMixin'
import OrderItemForm from 'src/modules/register/components/order-item-form/OrderItemForm'

// This is a hacky fix to prevent the price calculation from causing a recursive loop by using unreactive cache object
const ITEM_PRICES_BY_ITEM_IDS = {}

function clearItemPriceCache () {
  _.each(ITEM_PRICES_BY_ITEM_IDS, (_value, key) => {
    delete ITEM_PRICES_BY_ITEM_IDS[key]
  })
}

export default {
  name: 'FeaturedItemCard',
  components: {
    OrderItemForm,
    AgeRestrictedBanner,
    AlcoholAcknowledgementDialog
  },
  mixins: [SalesRestrictionsMixin],
  configuration: ['currentSite'],
  props: {
    item: {
      type: Object,
      required: true
    },
    order: {
      type: Object,
      required: true
    },
    dialog: {
      type: Boolean,
      required: false,
      default: true
    },
    availableOnline: {
      type: Boolean,
      required: false,
      default: false
    },
    hasAlcoholAcknowledgement: {
      type: Boolean,
      required: false,
      default: false
    },
    shown: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    width () {
      if (this.dialog || this.$q.screen.lt.sm) return '100%'
      if (this.$q.screen.lt.md) return '63%'
      if (this.$q.screen.lt.lg) return '45%'
      return '36%'
    }
  },

  created () {
    clearItemPriceCache()
  },
  methods: {
    itemCount (item) {
      return _.filter(this.order.orderItems.data, orderItem => orderItem.itemOrderItem?.itemId === item.id).length
    },
    priceForItem (item) {
      // Calculating prices on each render causes a recursive loop, so we cache the prices
      // after calculating them once.
      if (!_.isNil(ITEM_PRICES_BY_ITEM_IDS[item.id])) return ITEM_PRICES_BY_ITEM_IDS[item.id]

      let orderItem
      if (item.itemPreference) {
        orderItem = this.order.getFavoriteOrderItem(item.itemPreference)
      } else {
        orderItem = this.order.itemOrderItemForItem(item)
      }
      const price = orderItem?.displayPrice || 0
      ITEM_PRICES_BY_ITEM_IDS[item.id] = price
      orderItem?.destroy()

      return price
    },
    addItem (item) {
      const acknowledgeCookies = Cookies.get(this.$constants.cookies.alcoholAcknowledgement)
      const acknowledgeCookiesSiteId = acknowledgeCookies?.siteId

      const dontAskAgainConfirmed = !!acknowledgeCookies && acknowledgeCookiesSiteId === this.currentSite.id

      if (this.item.showAlcoholAcknowledgement && !dontAskAgainConfirmed && !this.$register) {
        this.$refs.alcoholAcknowledgementDialog.show()
      } else {
        this.checkItemSalesRestrictions(item)
      }
    },

    checkItemSalesRestrictions (item) {
      this.checkForSalesRestrictions({
        item,
        successCallback: (adminApproved = false) => {
          let orderItem
          if (item.itemPreference) {
            orderItem = this.order.getFavoriteOrderItem(item.itemPreference)
          } else {
            orderItem = this.order.itemOrderItemForItem(item, null, null, adminApproved)
          }

          // Skip showing detail on register since they can see the cart and can easily edit if needed
          if (item.skipDetailDialog) {
            this.order.orderItems.add(orderItem)
            orderItem.order = this.order
            this.notifyAdded(item)
          } else {
            orderItem.order = this.order
            this.$refs.orderItemForm.setCollection(this.order.orderItems)
            this.$refs.orderItemForm.show(orderItem)
          }
        }
      })
    },
    notifyAdded (item) {
      if (this.dialog) {
        this.shown = false // Close the dialog after adding the item to the cart
      }
      if (this.$register) return
      Notify.create({
        message: `${item.name} added to Cart`,
        type: 'positive',
        position: 'bottom',
        group: false,
        timeout: 2500,
        classes: 'added-to-cart-notif'
      })
    },

    descriptionForItem (item) {
      if (!item.itemPreference) {
        return item.name
      }

      const orderItem = this.order.getFavoriteOrderItem(item.itemPreference)

      let elements = [orderItem.itemOrderItem.item.name]

      if (item.itemVariations.data.length > 1) {
        elements.push('-')
        elements.push(orderItem.itemOrderItem.itemVariation.name)
      }

      if (orderItem.itemOrderItem.itemModifiers?.data?.length) {
        elements.push('-')
        elements = [...elements, _.map(orderItem.itemOrderItem.itemModifiers.data, 'modifier.name').join(', ')]
      }

      return elements.join(' ')
    }
  }
}
</script>

<style lang="scss" scoped>
.added-to-cart-notif {
  margin-bottom: 60px;
  margin-top: -50px;
}
</style>
